import { IIssue, IRetailer } from './marketplace-product'
import { MarketplaceCoupon } from '@scentregroup/shared/types'
import { IProductImage } from './product'
import { IError } from './error'
import { IMarketplaceProductPromotion } from '@scentregroup/shared/types'

export enum CartItemTypes {
  GENERIC_ITEM = 'GenericCartItem',
  GIFT_CARD_ITEM = 'GiftCardCartItem',
}

export interface IBaseCartItem {
  id: string
  productId: number
  sku: string
  salesPrice: number
  name: string
  image?: IProductImage
  imageAltText?: string
}

export interface IServiceFeeCartItem extends IBaseCartItem {
  description: string[]
  quantity: number
}

export interface IGiftCardCartItem extends IBaseCartItem {
  value: number
  recipientDetails: IRecipientDetails
}

export interface IMarketplaceCartItemShop {
  retailer?: IRetailer
  id: number
  name: string
}

export interface IMarketplaceCartItemOptionValue {
  id: string
  optionId: string
  displayName: string
  value: string
}

export interface IMarketplaceCartItem {
  id: string
  sku: string
  variantGroupCode?: string
  shop: IMarketplaceCartItemShop
  brand: string
  category: {
    path: {
      code: string
      title: string
    }[]
  } | null
  name: string
  image?: IProductImage
  listPrice: number
  discountPrice?: number
  quantity: number
  optionValues: IMarketplaceCartItemOptionValue[]
  variant: {
    quantity: number
    returnable: boolean | undefined
    promotions?: IMarketplaceProductPromotion[]
  }
  issues: IIssue[]
  promotions?: IMarketplaceProductPromotion[]
  coupon?: MarketplaceCoupon
}

// currently the same as IServiceFeeCartItem but may change
export interface IProductCartItem extends IBaseCartItem {
  description: string[]
  quantity: number
  options: ICartItemOption[]
}

export type ICartItem =
  | IGiftCardCartItem
  | IServiceFeeCartItem
  | IProductCartItem

export enum DeliveryMethods {
  EMAIL = 'EMAIL',
}

export interface IRecipientDetails {
  recipientName?: string
  senderName?: string
  message?: string
  recipientEmail: string
  deliveryDate: string
  deliveryMethod: DeliveryMethods
}

export interface ICart {
  id: string
  cartAmount: number
  shippingCost: number
  currency: string
  items: ICartItem[]
  marketplaceItems: IMarketplaceCartItem[]
  coupons?: MarketplaceCoupon[]
}

export interface ICartItemOption {
  id: number
  name: string
  value: string
}

interface IAddToCartMutationResultItem {
  id: string
  sku: string
  name: string
  listPrice?: number
  salesPrice: number
  productId: number
  quantity: number
  image: IProductImage
  options?: ICartItemOption[]
  __typename: string
}

export interface IAddToCartResponse {
  addProductToCart: IAddToCartMutationResult | IError
}

export function isAddToCartMutationResult(
  a: IAddToCartMutationResult | IError
): a is IAddToCartMutationResult {
  return (a as IAddToCartMutationResult).id !== undefined
}

export interface IAddToCartMutationResult {
  id: string
  cartAmount: number
  items: IAddToCartMutationResultItem[]
  marketplaceItems: IMarketplaceCartItem[]
}

export interface IInvalidInputErrorField {
  property: string
  errorCode: number
  message: string
}

export function isIGiftCardCartItem(arg: unknown): arg is IGiftCardCartItem {
  return (arg as IGiftCardCartItem).recipientDetails !== undefined
}

export function isIServiceFeeCartItem(
  arg: ICartItem
): arg is IServiceFeeCartItem {
  return (arg as IServiceFeeCartItem).quantity !== undefined
}

export function isIMarketplaceCartItem(
  arg: unknown
): arg is IMarketplaceCartItem {
  return (arg as IMarketplaceCartItem).shop !== undefined
}

export function isMarketplaceCartItem(
  arg: IMarketplaceCartItem
): arg is IMarketplaceCartItem {
  return (arg as IMarketplaceCartItem).shop !== undefined
}

export function isICart(arg: unknown): arg is ICart {
  return (arg as ICart)?.id !== undefined
}

export function isIAddToCartMutationResult(
  arg: IAddToCartMutationResult | IError
): arg is IAddToCartMutationResult {
  return (
    (arg as IAddToCartMutationResult)?.id !== undefined &&
    ((arg as IAddToCartMutationResult)?.items !== undefined ||
      (arg as IAddToCartMutationResult)?.marketplaceItems !== undefined)
  )
}

export function isIGetCartResult(arg: ICart | IError): arg is ICart {
  return (
    (arg as ICart)?.id !== undefined &&
    ((arg as ICart)?.items !== undefined ||
      (arg as ICart)?.marketplaceItems !== undefined)
  )
}

export const getCartItemCount = (cart: IAddToCartMutationResult): string =>
  cart.items
    .filter(item => item.__typename === CartItemTypes.GIFT_CARD_ITEM)
    .length.toString()

export const getCartItemMkpCount = (cart: IAddToCartMutationResult): string =>
  cart.marketplaceItems.reduce((a, b) => a + b.quantity, 0).toString()
